import React, { useEffect, useState, useRef } from "react";
import useMainContentDimensions from "../../../utils/mainContentDimensions";
import {
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Box,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import useWindowDimensions from "../../../utils/windowDimensions";
import useBalanceStatement from "../../../hooks/useBalanceStatement";
import useAuth from "../../../hooks/useAuth";

function BalanceSheet() {
  const theme = useTheme();

  // Media queries for responsiveness
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md")); // Tablet devices
  const isLaptopM = useMediaQuery(theme.breakpoints.between("md", "lg")); // Laptop Medium (1280px–1440px)
  const isLaptopL = useMediaQuery(theme.breakpoints.up("lg")); // Laptop Large (1440px and above)

  const { user } = useAuth();

  const { width: mainContentWidth } = useMainContentDimensions();

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 5,
  });

  const currentPayload = {
    filters: [],
    pagination: {
      pageNumber: 1,
      pageSize: 5,
    },
    sortFields: [
      {
        field: "createdAt",
        sort: "DESC",
      },
    ],
  };

  const [sortModel, setSortModel] = useState([
    {
      field: "updatedAt",
      sort: "desc",
    },
  ]);
  const { height } = useWindowDimensions();
  const { getBalanceReport, balanceReports, paging } = useBalanceStatement();

  const { totalNumberOfRecords, hasNextPage, pageNumber, pageSize } = paging;

  const [payload, setPayload] = useState(currentPayload);

  const fetchData = async () => {
    if (Object.keys(payload).length != 0) {
      await getBalanceReport(payload);
    }
  };

  const onPageChange = (newPaginationModel) => {
    console.log(newPaginationModel, "pagepage");
    const pagination = {
      pageNumber: newPaginationModel.page == 0 ? 1 : newPaginationModel.page + 1,
      pageSize: payload.pagination.pageSize,
    };
    setPayload((prev) => ({ ...prev, pagination }));
    setPaginationModel(newPaginationModel);
  };

  useEffect(() => {
    const fetchRealTimeData = async () => {
      const filters = [];
      if (user && user.businessType !== "SUPER_ADMIN" && user.tenantId) {
        filters.push({
          fieldName: "tenantId",
          fieldValue: user.tenantId,
          op: "eq",
          dataType: "long",
          condition: "and",
          isJoin: false,
        });
      }

      const newPayload = {
        filters,
        pagination: { pageNumber: 1, pageSize: 5 },
        sortFields: [{ fieldName: "createdAt", sortOrder: "DESC" }],
      };

      setPayload(newPayload);
      await getBalanceReport(newPayload);
    };

    if (user) {
      fetchRealTimeData();
    }
  }, [user]);

  useEffect(() => {
    if (payload !== currentPayload) {
      fetchData();
    }
  }, [payload]);

  const handleSortModelChange = (newModel) => {
    console.log(newModel, "newModel");
    if (newModel.length !== 0) {
      setSortModel(newModel);
      setPayload((prev) => ({ ...prev, sortFields: newModel }));
    }
  };

  const columns = [
    {
      field: "tenantName",
      headerName: "Franchise Name",
      width: isTablet ? 150 : isLaptopM ? 200 : isLaptopL ? 220 : 180, // Adjust column width
    },
    {
      field: "openingBalance",
      headerName: "Opening Bal",
      width: isTablet ? 140 : isLaptopM ? 180 : isLaptopL ? 200 : 160, // Adjust column width
    },
    {
      field: "closingBalance",
      headerName: "Closing Bal",
      width: isTablet ? 130 : isLaptopM ? 160 : isLaptopL ? 180 : 160, // Adjust column width
    },
  ];

  const paginationMetaRef = useRef();

  // Memoize to avoid flickering when the `hasNextPage` is `undefined` during refetch
  const paginationMeta = React.useMemo(() => {
    if (hasNextPage !== undefined && paginationMetaRef.current?.hasNextPage !== hasNextPage) {
      paginationMetaRef.current = { hasNextPage };
    }
    return paginationMetaRef.current;
  }, [hasNextPage]);

  return (
    <Grid item xs={12} md={6}>
      <Card
        sx={{
          width: "100%",
          borderRadius: "20px",
          backgroundColor: (theme) => theme.palette.background.paper,
          boxShadow: (theme) =>
            theme.palette.mode === "dark"
              ? "0 10px 40px -10px rgba(0, 0, 0, 0.4)"
              : "0 10px 40px -10px rgba(0, 0, 0, 0.1)",
          overflow: "hidden",
          border: (theme) =>
            theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "none",
        }}
      >
        <Box sx={{ p: { xs: 3, sm: 4 } }}>
          <Box sx={{ mb: 4 }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: "text.primary" }}>
              Balance Statement
            </Typography>
          </Box>
          <Box sx={{ width: "100%" }}>
            <DataGrid
              autoHeight
              initialState={{
                pagination: { paginationModel: { pageSize: 5 } },
              }}
              columns={columns}
              rows={balanceReports}
              refreshPage={fetchData}
              getRowId={(row) => row["tenantId"]}
              rowCount={paging?.totalNumberOfRecords}
              loading={false}
              rowsPerPageOptions={[5]}
              pagination
              page={paging?.pageNumber}
              pageSize={5}
              paginationMode="server"
              onPaginationModelChange={onPageChange}
              paginationMeta={paginationMeta}
              sortingMode="server"
              sortModel={sortModel}
              onSortModelChange={handleSortModelChange}
              paginationModel={paginationModel}
              sx={{
                border: "none",
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.05)" : "#f8fafc",
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid #f1f5f9",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.8)" : "#64748b",
                  fontSize: "12px",
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.05em",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: (theme) =>
                    theme.palette.mode === "dark"
                      ? "1px solid rgba(255, 255, 255, 0.08)"
                      : "1px solid #f1f5f9",
                  fontSize: "14px",
                  color: "text.primary",
                },
                "& .MuiDataGrid-row:hover": {
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.04)" : "#f8fafc",
                },
              }}
              getRowHeight={() => 64}
            />
          </Box>
        </Box>
      </Card>
    </Grid>
  );
}

export default BalanceSheet;
